'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { List, X } from '@phosphor-icons/react'
import { TextColumns, MathOperations, Calculator, CaretRight } from '@phosphor-icons/react/dist/ssr'
import { CometLogo } from './CometLogo'

interface CometNavItem {
  label: string
  href: string
  external?: boolean
  hasDropdown?: boolean
}

interface CometNavBarProps {
  items?: CometNavItem[]
  logoHref?: string
  demoLabel?: string
  demoHref?: string
}

interface ResourceLibraryItem {
  label: string
  href: string
  external?: boolean
  icon: React.ReactNode
}

const LIBRARY_ITEMS: ResourceLibraryItem[] = [
  { label: 'Blog',                   href: '/comet/blogs',                              icon: <TextColumns size={20} /> },
  { label: 'ROI Calculator',         href: 'https://comethealth.ai/roi-calculator',     external: true, icon: <MathOperations size={20} /> },
  { label: 'AI Maturity Calculator', href: 'https://comethealth.ai/ai-maturity-tool',   external: true, icon: <Calculator size={20} /> },
]

// Flagship / featured article — shared by the desktop dropdown and the mobile drawer
const FEATURED = {
  title: 'Integrating AI Access Tools with Epic: A Conversation with Team Comet',
  href: '/comet/blogs',
  bgSrc: '/media/comet/featured-bg.png',
  docSrc: '/media/comet/featured-epic.png',
}

// Fix 3: updated nav labels + hrefs from user's screenshot
const DEFAULT_ITEMS: CometNavItem[] = [
  { label: 'PROVIDER SOLUTION',   href: 'https://comethealth.ai/ai-call-center',            external: true },
  { label: 'SPECIALITY SOLUTION', href: 'https://comethealth.ai/speciality-ai-call-center', external: true },
  { label: 'RESOURCES',           href: '#',                                                 hasDropdown: true },
  { label: 'PILOT PROGRAM',       href: 'https://comethealth.ai/pilot-program',              external: true },
  { label: 'INNOVACCER',          href: 'https://innovaccer.com/',                           external: true },
]

function ResourcesDropdown({ onClose }: { onClose: () => void }) {
  return (
    <div className="comet-resources-dropdown__inner">
      {/* Featured column */}
      <div className="comet-res-featured">
        <p className="comet-res-section-label">Flagship</p>
        <hr className="comet-res-divider" />
        <a href={FEATURED.href} className="comet-res-featured__card" onClick={onClose}>
          <div className="comet-res-featured__title-row">
            <span className="comet-res-featured__title">
              {FEATURED.title}
            </span>
            <span className="comet-res-featured__arrow-icon" aria-hidden="true">
              <CaretRight size={14} weight="fill" color="#000A1B" />
            </span>
          </div>
          <div className="comet-res-featured__image">
            <div className="comet-res-featured__bg-layer">
              <img src={FEATURED.bgSrc} alt="" />
            </div>
            <div className="comet-res-featured__doc-layer--landscape">
              <img src={FEATURED.docSrc} alt={FEATURED.title} />
            </div>
          </div>
        </a>
      </div>

      {/* Library column */}
      <div className="comet-res-library">
        <p className="comet-res-section-label">Library</p>
        <hr className="comet-res-divider" />
        <div className="comet-res-library__items">
          {LIBRARY_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="comet-res-library__item"
              onClick={onClose}
              {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              <span className="comet-res-library__icon" aria-hidden="true">
                {item.icon}
              </span>
              <span className="comet-res-library__name">{item.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}

export function CometNavBar({
  items = DEFAULT_ITEMS,
  logoHref = 'https://comethealth.ai',
  demoLabel = 'REQUEST A DEMO',
  demoHref = 'https://comethealth.ai/request-a-demo',
}: CometNavBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [panelTop, setPanelTop]       = useState(0)
  const [panelCenter, setPanelCenter] = useState(0)
  const closeTimer     = useRef<ReturnType<typeof setTimeout> | null>(null)
  const headerRef      = useRef<HTMLDivElement>(null)
  const resTriggerRef  = useRef<HTMLDivElement>(null)

  useEffect(() => { setMounted(true) }, [])

  const openResources = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    if (headerRef.current) {
      setPanelTop(Math.round(headerRef.current.getBoundingClientRect().bottom))
    }
    // Center dropdown under the RESOURCES trigger midpoint
    if (resTriggerRef.current) {
      const r = resTriggerRef.current.getBoundingClientRect()
      setPanelCenter(Math.round(r.left + r.width / 2))
    }
    setResourcesOpen(true)
  }, [])

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setResourcesOpen(false), 250)
  }, [])

  return (
    <>
    <header className="comet-nav" style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
      <div className="comet-nav__inner" ref={headerRef}>
        {/* Left: logo + menu */}
        <div className="comet-nav__left">
          <a href={logoHref} className="comet-nav__logo" aria-label="Comet by Innovaccer">
            <CometLogo height={36} />
          </a>
          <nav className="comet-nav__menu" aria-label="Primary">
            {items.map((item) => {
              if (item.hasDropdown) {
                return (
                  <div
                    key={item.label}
                    ref={resTriggerRef}
                    className="comet-nav__resources-trigger"
                    onMouseEnter={openResources}
                    onMouseLeave={scheduleClose}
                  >
                    <button
                      type="button"
                      className={`comet-nav__item comet-nav__item--btn${resourcesOpen ? ' comet-nav__item--active' : ''}`}
                      aria-haspopup="true"
                      aria-expanded={resourcesOpen}
                    >
                      {item.label}
                    </button>
                  </div>
                )
              }
              return item.external
                ? <a key={item.label} href={item.href} className="comet-nav__item" target="_blank" rel="noopener noreferrer">{item.label}</a>
                : <Link key={item.label} href={item.href} className="comet-nav__item">{item.label}</Link>
            })}
          </nav>
        </div>

        {/* Right: single CTA + hamburger (Fix 3: only REQUEST A DEMO) */}
        <div className="comet-nav__right">
          <a href={demoHref} className="comet-nav__cta comet-nav__cta--outline" target="_blank" rel="noopener noreferrer">
            <span className="comet-nav__cta__text">
              <span className="btn-primary-text">{demoLabel}</span>
              <span className="btn-hover-text" aria-hidden="true">{demoLabel}</span>
            </span>
          </a>
          <button
            type="button"
            className="comet-nav__hamburger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`comet-nav__mobile${mobileOpen ? ' is-open' : ''}`}>
        <nav className="comet-nav__mobile-menu" aria-label="Mobile primary">
          {items.map((item) => {
            if (item.hasDropdown) {
              return (
                <div key="RESOURCES" className="comet-nav__mobile-resources">
                  <button
                    type="button"
                    className="comet-nav__mobile-resources-btn"
                    onClick={() => setMobileResourcesOpen((o) => !o)}
                    aria-expanded={mobileResourcesOpen}
                  >
                    <span className="comet-nav__mobile-nav-item__label">RESOURCES</span>
                    <span className={`comet-nav__mobile-resources-chevron${mobileResourcesOpen ? ' is-open' : ''}`} aria-hidden="true">▶</span>
                  </button>
                  {mobileResourcesOpen && (
                    <div className="comet-nav__mobile-resources-list">
                      {/* Library section */}
                      <p className="comet-nav__mobile-res-label">Library</p>
                      <hr className="comet-nav__mobile-res-divider" />
                      {LIBRARY_ITEMS.map((res) => (
                        <a
                          key={res.label}
                          href={res.href}
                          className="comet-nav__mobile-resource-item"
                          onClick={() => { setMobileOpen(false); setMobileResourcesOpen(false) }}
                          {...(res.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          <span className="comet-nav__mobile-resource-icon" aria-hidden="true">{res.icon}</span>
                          <span className="comet-nav__mobile-resource-name">{res.label}</span>
                        </a>
                      ))}

                      {/* Flagship (featured) section */}
                      <p className="comet-nav__mobile-res-label comet-nav__mobile-res-label--flagship">Flagship</p>
                      <a
                        href={FEATURED.href}
                        className="comet-nav__mobile-flagship"
                        onClick={() => { setMobileOpen(false); setMobileResourcesOpen(false) }}
                      >
                        <span className="comet-nav__mobile-flagship__title">{FEATURED.title}</span>
                        <span className="comet-nav__mobile-flagship__arrow" aria-hidden="true">
                          <CaretRight size={16} weight="fill" color="#000A1B" />
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              )
            }
            return item.external
              ? <a key={item.label} href={item.href} className="comet-nav__mobile-nav-item" onClick={() => setMobileOpen(false)} target="_blank" rel="noopener noreferrer">
                  <span className="comet-nav__mobile-nav-item__label">{item.label}</span>
                </a>
              : <Link key={item.label} href={item.href} className="comet-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
                  <span className="comet-nav__mobile-nav-item__label">{item.label}</span>
                </Link>
          })}
        </nav>

        <div className="comet-nav__mobile-ctas">
          <a href={demoHref} className="comet-nav__cta comet-nav__cta--outline comet-nav__cta--block" onClick={() => setMobileOpen(false)}>
            {demoLabel}
          </a>
        </div>
      </div>
    </header>

    {/* Desktop resources dropdown — portalled, centered under RESOURCES trigger */}
    {mounted && resourcesOpen && createPortal(
      <div
        style={{
          position: 'fixed',
          top: panelTop,
          left: panelCenter,
          transform: 'translateX(-50%)',
          zIndex: 1001,
          paddingTop: '8px',
        }}
        onMouseEnter={openResources}
        onMouseLeave={scheduleClose}
        role="dialog"
        aria-label="Resources"
      >
        <ResourcesDropdown onClose={() => setResourcesOpen(false)} />
      </div>,
      document.body
    )}

    {/* Blur backdrop */}
    {mounted && resourcesOpen && createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          top: panelTop,
          zIndex: 999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          background: 'rgba(0, 0, 0, 0.10)',
          pointerEvents: 'auto',
        }}
        onClick={() => setResourcesOpen(false)}
        aria-hidden="true"
      />,
      document.body
    )}
  </>
  )
}

export default CometNavBar
